import { useCallback, useEffect, useState } from 'react'

export type Ladezustand<T> =
  | { art: 'laedt' }
  | { art: 'da'; wert: T }
  | { art: 'fehler'; meldung: string }

/** Lädt einmal und auf Zuruf erneut. */
export function useLaden<T>(
  holen: () => Promise<T>,
  abhaengigkeiten: unknown[] = [],
): [Ladezustand<T>, () => void] {
  const [zustand, setZustand] = useState<Ladezustand<T>>({ art: 'laedt' })
  const [runde, setRunde] = useState(0)

  const neuLaden = useCallback(() => setRunde((r) => r + 1), [])

  useEffect(() => {
    let abgebrochen = false

    holen()
      .then((wert) => {
        if (!abgebrochen) setZustand({ art: 'da', wert })
      })
      .catch((fehler: unknown) => {
        if (abgebrochen) return
        setZustand({
          art: 'fehler',
          meldung: fehler instanceof Error ? fehler.message : 'Unbekannter Fehler',
        })
      })

    return () => {
      abgebrochen = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runde, ...abhaengigkeiten])

  return [zustand, neuLaden]
}

/** „seit 3 Tagen" — für die älteste wartende Sache je Schlange (§8.6). */
export function seit(zeitpunkt: string | null): string {
  if (!zeitpunkt) return '—'
  const dann = new Date(zeitpunkt).getTime()
  if (!Number.isFinite(dann)) return '—'

  const minuten = Math.floor((Date.now() - dann) / 60000)
  if (minuten < 1) return 'gerade eben'
  if (minuten < 60) return `seit ${minuten} min`

  const stunden = Math.floor(minuten / 60)
  if (stunden < 24) return `seit ${stunden} ${stunden === 1 ? 'Stunde' : 'Stunden'}`

  const tage = Math.floor(stunden / 24)
  if (tage < 31) return `seit ${tage} ${tage === 1 ? 'Tag' : 'Tagen'}`

  const monate = Math.floor(tage / 30)
  return `seit ${monate} ${monate === 1 ? 'Monat' : 'Monaten'}`
}

/** Datum und Uhrzeit — bei mehreren Entscheidungen am Tag sagt das Datum nichts. */
export function zeitpunktText(zeitpunkt: string | null): string {
  if (!zeitpunkt) return '—'
  const d = new Date(zeitpunkt)
  if (!Number.isFinite(d.getTime())) return '—'
  const heute = new Date()
  const gleicherTag =
    d.getFullYear() === heute.getFullYear() &&
    d.getMonth() === heute.getMonth() &&
    d.getDate() === heute.getDate()
  const uhr = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
  // Heute ist das Datum überflüssig; die Uhrzeit ist die Information.
  return gleicherTag
    ? `heute ${uhr}`
    : `${d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} ${uhr}`
}

export function datum(zeitpunkt: string | null): string {
  if (!zeitpunkt) return '—'
  const d = new Date(zeitpunkt)
  return Number.isFinite(d.getTime())
    ? d.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '—'
}

/** „+3 für Marie" — was an Punkten geflossen ist (§10). */
export function punkteSatz(
  differenz: number | null | undefined,
  name: string,
): string {
  if (differenz === null || differenz === undefined || differenz === 0) {
    return 'keine Punkte verändert'
  }
  return `${differenz > 0 ? '+' : ''}${differenz} für ${name}`
}
